// OpsAccountsPage — Ops › Accounts (pen Q67), pure.
//
// Everyone who can sign in: create an account by email, disable or enable
// it, delete it. One action per intent. Delete names what goes and asks for
// the account's email typed back (SF-9). The operator's own row shows "You"
// and no actions (FR-12.8). Emails are untrusted text: React text only.
// The email being typed and which row is confirming a delete are view
// state; the accounts and every change come from and go to the host.
// "AI today" is each account's AI cost since midnight UTC against its daily
// ceiling, and the line above the table the same for everyone (QA-14).

export type { AccountsNotice };
export { OpsAccountsPage };

import type { OpsAccount, OpsAccounts } from "@bilda/app/ops";
import * as Core from "@bilda/core";
import { exhaustSwitchCase, type Remote } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { formatMoney } from "../../app/aiUsage/format.js";
import type { ExternalError } from "../../app/externalError.js";
import { TableSkeleton } from "../AiUsage/AiUsageStates.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { Input } from "../Input/Input.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import { latest } from "./feedbackParts.js";
import {
    type OpsAccess,
    type OpsFrame,
    OpsLoadFailed,
    OpsMenu,
    OpsNoAccess,
} from "./OpsMenu.js";

// What the last action did: created, refused by name, or failed.
type AccountsNotice =
    | { readonly type: "created"; readonly email: string }
    | { readonly type: "refused"; readonly reason: string }
    | { readonly type: "failed" };

type OpsAccountsPageProps = {
    readonly frame: OpsFrame;
    readonly access: OpsAccess;
    readonly accounts: Remote.Remote<OpsAccounts, ExternalError>;
    readonly notice: AccountsNotice | undefined;
    // An action on its way (create, or one account's id).
    readonly busy: boolean;
    readonly onRetry: () => void;
    readonly onCreate: (email: string) => void;
    readonly onDisable: (account: OpsAccount) => void;
    readonly onEnable: (account: OpsAccount) => void;
    readonly onRemove: (account: OpsAccount, confirmEmail: string) => void;
};

const useNoticeLine = (): ((n: AccountsNotice) => string) => {
    const { t } = useLingui();
    const refusal = (reason: string): string => {
        switch (reason) {
            case "account_email_invalid":
                return t`That is not a valid email address.`;
            case "account_email_taken":
                return t`An account with that email already exists.`;
            case "self_disable_refused":
                return t`You cannot disable your own account.`;
            case "self_delete_refused":
                return t`You cannot delete your own account.`;
            case "delete_not_confirmed":
                return t`Not deleted: the typed email did not match.`;
            case "account_already_disabled":
                return t`The account is already disabled.`;
            case "account_not_disabled":
                return t`The account is already active.`;
            case "user_not_found":
                return t`The account no longer exists.`;
            default:
                return t`That did not go through. Nothing was changed.`;
        }
    };
    return (n) => {
        switch (n.type) {
            case "created":
                return t`Created ${n.email}. They sign in with that email and land in their own workspace.`;
            case "refused":
                return refusal(n.reason);
            case "failed":
                return t`That did not go through. Nothing was changed.`;
            default:
                return exhaustSwitchCase(n);
        }
    };
};

const Row = ({
    account,
    accountCeiling,
    isSelf,
    busy,
    onDisable,
    onEnable,
    onRemove,
}: {
    readonly account: OpsAccount;
    readonly accountCeiling: number;
    readonly isSelf: boolean;
    readonly busy: boolean;
    readonly onDisable: (account: OpsAccount) => void;
    readonly onEnable: (account: OpsAccount) => void;
    readonly onRemove: (account: OpsAccount, confirmEmail: string) => void;
}) => {
    const { t, i18n } = useLingui();
    const [confirming, setConfirming] = useState(false);
    const [typed, setTyped] = useState("");
    const day = (iso: string | null): string =>
        iso === null
            ? "—"
            : new Intl.DateTimeFormat(i18n.locale, {
                  day: "numeric",
                  month: "short",
              }).format(new Date(iso));
    const disabled = account.disabledAt !== null;
    return (
        <>
            <tr className={disabled ? "ops__row--disabled" : undefined}>
                <td>{account.email}</td>
                <td>{day(account.createdAt)}</td>
                <td>{day(account.lastSignInAt)}</td>
                <td>
                    <AiToday
                        spent={account.aiSpentTodayMicroUsd}
                        ceiling={accountCeiling}
                    />
                </td>
                <td>
                    {account.isAdmin ? <strong>{t`Operator`}</strong> : "—"}
                </td>
                <td>
                    {disabled ? (
                        <span className="ops__tag ops__tag--warning">{t`Disabled`}</span>
                    ) : (
                        <span className="ops__tag ops__tag--neutral">{t`Active`}</span>
                    )}
                </td>
                <td>
                    {isSelf ? (
                        <span className="ops__actions ops__faint">{t`You`}</span>
                    ) : (
                        <span className="ops__actions">
                            {disabled ? (
                                <Button
                                    size="sm"
                                    disabled={busy}
                                    onClick={() => onEnable(account)}
                                >
                                    {t`Enable`}
                                </Button>
                            ) : (
                                <Button
                                    size="sm"
                                    disabled={busy}
                                    onClick={() => onDisable(account)}
                                >
                                    {t`Disable`}
                                </Button>
                            )}
                            <Button
                                size="sm"
                                variant="danger"
                                disabled={busy}
                                aria-expanded={confirming}
                                onClick={() => setConfirming((c) => !c)}
                            >
                                {t`Delete`}
                            </Button>
                        </span>
                    )}
                </td>
            </tr>
            {confirming && !isSelf ? (
                <tr className="ops__detail">
                    <td colSpan={7}>
                        <form
                            className="ops__confirm"
                            onSubmit={(e) => {
                                e.preventDefault();
                                onRemove(account, typed);
                            }}
                        >
                            <span>
                                {t`Delete ${account.email} and their workspace for good? Type the email to confirm.`}
                            </span>
                            <Input
                                size="sm"
                                type="email"
                                value={typed}
                                aria-label={t`Email to confirm`}
                                onChange={(e) => setTyped(e.target.value)}
                            />
                            <Button
                                size="sm"
                                variant="danger"
                                type="submit"
                                disabled={busy || typed.trim() === ""}
                            >
                                {t`Delete for good`}
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setConfirming(false)}
                            >
                                {t`Cancel`}
                            </Button>
                        </form>
                    </td>
                </tr>
            ) : null}
        </>
    );
};

// When the day turns for the ceilings, as HH:MM UTC.
const RESET_TIME = new Date(
    Core.AgentCall.DailyCeiling.DAY_BOUNDARY_UTC_OFFSET_MS,
)
    .toISOString()
    .slice(11, 16);

const AiToday = ({ spent, ceiling }: { spent: number; ceiling: number }) => {
    const { t, i18n } = useLingui();
    const of = `${formatMoney(i18n.locale, spent)} / ${formatMoney(i18n.locale, ceiling)}`;
    return (
        <>
            {of}
            {spent >= ceiling ? (
                <>
                    {" "}
                    <span className="ops__tag ops__tag--warning">{t`Limit reached`}</span>
                </>
            ) : null}
        </>
    );
};

const OpsAccountsPage = (props: OpsAccountsPageProps) => {
    const { t } = useLingui();
    const noticeLine = useNoticeLine();
    const [email, setEmail] = useState("");
    const value = latest(props.accounts);
    return (
        <div className="ai-usage">
            <OpsMenu
                frame={props.frame}
                access={props.access}
                current="accounts"
            />
            <main className="ai-usage__main">
                {props.access === "denied" ? (
                    <OpsNoAccess />
                ) : (
                    <>
                        <header className="ai-usage__heading">
                            <h1 className="ops__title">{t`Accounts`}</h1>
                            <p className="ai-usage__lede">
                                {t`Everyone who can sign in. Each account has its own workspace.`}
                            </p>
                        </header>
                        <div className="ops__section">
                            <form
                                className="ops__create"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    props.onCreate(email);
                                    setEmail("");
                                }}
                            >
                                <Input
                                    type="email"
                                    value={email}
                                    placeholder={t`Email of the person to let in`}
                                    aria-label={t`Email of the person to let in`}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={props.busy || email.trim() === ""}
                                >
                                    {t`Create account`}
                                </Button>
                            </form>
                            {props.notice === undefined ? null : (
                                <p
                                    className={`ops__notice ops__notice--${props.notice.type === "created" ? "success" : "danger"}`}
                                    role="status"
                                >
                                    <Icon
                                        name={
                                            props.notice.type === "created"
                                                ? "check"
                                                : "alert"
                                        }
                                        size="sm"
                                    />
                                    {noticeLine(props.notice)}
                                </p>
                            )}
                            {props.accounts._tag === "Remote.Failed" ? (
                                <OpsLoadFailed
                                    title={t`Accounts could not be loaded`}
                                    onRetry={props.onRetry}
                                />
                            ) : value === undefined ? (
                                <TableSkeleton />
                            ) : (
                                <>
                                    <p className="ops__faint">
                                        {t`AI today, everyone:`}{" "}
                                        <AiToday
                                            spent={
                                                value.aiToday.deploymentMicroUsd
                                            }
                                            ceiling={
                                                value.aiToday
                                                    .deploymentCeilingMicroUsd
                                            }
                                        />{" "}
                                        {t`Daily limits reset at ${RESET_TIME} UTC.`}
                                    </p>
                                    <div className="ops__panel">
                                        <table className="ops__table ops__table--accounts">
                                            <thead>
                                                <tr>
                                                    <th scope="col">{t`Email`}</th>
                                                    <th scope="col">{t`Created`}</th>
                                                    <th scope="col">{t`Last session`}</th>
                                                    <th scope="col">{t`AI today`}</th>
                                                    <th scope="col">{t`Access`}</th>
                                                    <th scope="col">{t`Status`}</th>
                                                    <th scope="col">
                                                        <VisuallyHidden>{t`Actions`}</VisuallyHidden>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {value.users.map((u) => (
                                                    <Row
                                                        key={u.id}
                                                        account={u}
                                                        accountCeiling={
                                                            value.aiToday
                                                                .accountCeilingMicroUsd
                                                        }
                                                        isSelf={
                                                            u.id ===
                                                            value.selfId
                                                        }
                                                        busy={props.busy}
                                                        onDisable={
                                                            props.onDisable
                                                        }
                                                        onEnable={
                                                            props.onEnable
                                                        }
                                                        onRemove={
                                                            props.onRemove
                                                        }
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
};
